import { useId, useState } from 'react';
import { Code2, Info } from 'lucide-react';
import type { CodeCompositionStats } from '../../types/api/metrics';
import { Modal } from '../ui/Modal';
import CodeCompositionDonutChart, {
  CodeCompositionDonutChartSkeleton,
} from '../charts/CodeCompositionDonutChart';
import { CodeCompositionHelpContent } from './codeCompositionHelp';
import {
  buildBarSegments,
  formatCompositionPercent,
  formatLineCount,
} from './codeCompositionUtils';
import { useCodeCompositionQuery, type MetricsCardFetchScope } from './metricsCardQueries';

export type CodeCompositionFetchScope = MetricsCardFetchScope;

type CodeCompositionCardProps = {
  fetchScope?: MetricsCardFetchScope;
  className?: string;
};

function CodeCompositionMetricsRow({ composition }: { composition: CodeCompositionStats | null }) {
  const segments = buildBarSegments(composition);

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
      <div className="flex flex-col gap-0.5">
        <dt className="text-slate-400">New lines</dt>
        <dd className="font-medium text-slate-100 tabular-nums">
          {formatLineCount(segments.newLines)}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-slate-400">Existing lines</dt>
        <dd className="font-medium text-slate-100 tabular-nums">
          {formatLineCount(segments.existingLines)}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-slate-400">Rework lines</dt>
        <dd className="font-medium text-slate-100 tabular-nums">
          {formatLineCount(segments.reworkLines)}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-slate-400">Commits with stats</dt>
        <dd className="font-medium text-slate-100 tabular-nums">
          {composition?.commitsWithFileStats ?? 0}
          {composition?.commitCount != null ? ` / ${composition.commitCount}` : ''}
        </dd>
      </div>
    </dl>
  );
}

function CodeCompositionMetricsSkeleton() {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-pulse"
      aria-hidden
    >
      {Array.from({ length: 4 }, (_, i) => (
        <div key={`composition-metric-skeleton-${i}`} className="space-y-2">
          <div className="h-3 w-24 rounded bg-dark-elevated" />
          <div className="h-5 w-16 rounded bg-dark-elevated" />
        </div>
      ))}
    </div>
  );
}

/**
 * New-file vs existing-file composition from per-file diff stats (deployment-branch commits).
 */
export default function CodeCompositionCard({ fetchScope, className = '' }: CodeCompositionCardProps) {
  const [windowDays, setWindowDays] = useState(30);
  const [helpOpen, setHelpOpen] = useState(false);
  const infoButtonId = useId();

  const compositionQuery = useCodeCompositionQuery(fetchScope, windowDays);

  const composition: CodeCompositionStats | null = compositionQuery.data?.composition ?? null;
  const loading = compositionQuery.isPending;

  const segments = buildBarSegments(composition);
  const coverage = composition?.fileStatsCoverage;
  const showCoverage =
    coverage != null && composition?.commitCount != null && composition.commitCount > 0 && coverage < 100;

  const windowSelect = fetchScope?.orgId ? (
    <>
      <label htmlFor="code-composition-window-select" className="sr-only">
        Code composition window (days)
      </label>
      <select
        id="code-composition-window-select"
        value={String(windowDays)}
        onChange={(e) => setWindowDays(Number(e.target.value))}
        className="select-mdl py-2 min-w-[140px] text-sm"
        aria-label="Code composition window (days)"
      >
        <option value="7">7 days</option>
        <option value="30">30 days</option>
        <option value="365">365 days</option>
      </select>
    </>
  ) : null;

  const cardHeader = (
    <div className="flex flex-nowrap items-center gap-3 px-6 pt-6 pb-3 w-full shrink-0">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Code2 className="w-5 h-5 shrink-0 text-vivid-red" aria-hidden />
        <h3 className="truncate text-lg font-medium text-slate-100">Code composition</h3>
        <button
          type="button"
          id={infoButtonId}
          className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-slate-500 hover:text-red-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-vivid-red focus-visible:ring-offset-2 focus-visible:ring-offset-dark-surface"
          aria-label="Code composition: definition and methodology"
          onClick={() => setHelpOpen(true)}
        >
          <Info className="w-4 h-4" aria-hidden />
        </button>
      </div>
      {windowSelect ? <div className="ml-auto shrink-0">{windowSelect}</div> : null}
    </div>
  );

  const rootClass = ['mt-10', 'min-w-0', className].filter(Boolean).join(' ');

  const cardShellClass = 'card-mdl-static overflow-hidden h-full flex flex-col min-h-0';
  const chartAreaClass = 'flex-1 flex flex-col justify-center min-h-[280px] px-6 pb-4';

  if (loading) {
    return (
      <div className={rootClass}>
        <div className={cardShellClass}>
          {cardHeader}
          <div className={chartAreaClass}>
            <CodeCompositionDonutChartSkeleton />
          </div>
          <div className="px-6 py-4 border-t border-dark-border shrink-0">
            <CodeCompositionMetricsSkeleton />
          </div>
        </div>
      </div>
    );
  }

  const emptyMessage =
    'No added lines on deployment branches in this window. Sync repos or widen the time window.';

  return (
    <div className={rootClass}>
      <div className={cardShellClass}>
        {cardHeader}

        {segments.hasData ? (
          <div className={chartAreaClass}>
            <CodeCompositionDonutChart segments={segments} />
          </div>
        ) : (
          <div className={`${chartAreaClass} pb-2`}>
            <p className="text-sm text-slate-400 text-center py-8 border border-dashed border-dark-border rounded-lg">
              {emptyMessage}
            </p>
          </div>
        )}

        <div className="px-6 py-4 border-t border-dark-border shrink-0">
          <CodeCompositionMetricsRow composition={composition} />
        </div>

        {showCoverage ? (
          <div className="px-6 py-3 border-t border-dark-border bg-amber-500/5 shrink-0">
            <p className="text-xs text-amber-200/90">
              File stats available for {formatCompositionPercent(coverage)} of{' '}
              {composition?.commitCount ?? 0} deployment-branch commits
            </p>
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Code composition"
        icon={<Info className="w-5 h-5 text-vivid-red" aria-hidden />}
        contentClassName="max-w-lg"
      >
        <CodeCompositionHelpContent />
      </Modal>
    </div>
  );
}
